import {
  Modal,
  ModalSection,
  Dropdown,
  Tag,
  Button,
  TextField,
  Icon,
  IconButton,
} from '@monash/portal-react';
import c from './feedback-modal.module.scss';
import {
  UPDATE_FEEDBACK_STATUS,
  STATUS_FILTERS,
  PRIORITY_FILTERS,
  PRIORITY_TAGS,
  BUG_OPTIONS,
  HDR_OPTIONS,
} from '../constants';
import { useState, useContext } from 'react';
import { fsUpdateDoc, AuthContext } from '@monash/portal-frontend-common';
import ConfirmationModal from './ConfirmationModal';

const FeedbackModal = ({
  editFeedbackShown,
  feedbackItem,
  setEditFeedbackShown,
}) => {
  const isResolved = feedbackItem.status === UPDATE_FEEDBACK_STATUS.RESOLVED;
  const [selectedStatus, setSelectedStatus] = useState(
    isResolved ? '' : feedbackItem.status || UPDATE_FEEDBACK_STATUS.NEW
  );
  const [selectedPriority, setSelectedPriority] = useState(
    feedbackItem.priority || ''
  );
  const [selectedBug, setSelectedBug] = useState(feedbackItem.bug || null);
  const [selectedHdr, setSelectedHdr] = useState(feedbackItem.hdr || null);
  const [jiraLinks, setJiraLinks] = useState(feedbackItem.jiraLinks || ['']);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const authCtx = useContext(AuthContext);

  const closeEditFeedback = () => {
    setConfirmDelete(false);
    setSelectedStatus(
      isResolved ? '' : feedbackItem.status || UPDATE_FEEDBACK_STATUS.NEW
    );
    setSelectedPriority(feedbackItem.priority || '');
    setSelectedBug(feedbackItem.bug || null);
    setSelectedHdr(feedbackItem.hdr || null);
    setJiraLinks(feedbackItem.jiraLink || ['']);
    setEditFeedbackShown(false);
  };

  const openConfirmDelete = () => {
    setConfirmDelete(true);
  };

  const closeConfirmDelete = () => {
    setConfirmDelete(false);
  };

  const priorityOptions = PRIORITY_FILTERS.map((value) => ({
    value,
    text: (
      <Tag
        text={PRIORITY_TAGS[value].label}
        color={PRIORITY_TAGS[value].colour}
      />
    ),
  }));

  const addLink = () => {
    setJiraLinks([...jiraLinks, '']);
  };

  const removeLink = (index) => {
    setJiraLinks((f) => {
      const newList = [...f];
      newList.splice(index, 1);
      return newList;
    });
  };

  const handleChange = (change, index) => {
    const newValue = change.target.value;
    setJiraLinks(
      jiraLinks.map((link, i) => (i === index ? `${newValue}` : link))
    );
  };

  const submitChanges = () => {
    const validation = [];
    // check if link has https:// at the start, if not, append. then write to firestore, should be all the selected values
    jiraLinks.forEach((link) => {
      if (link.startsWith('https://')) {
        validation.push(link);
      } else if (link !== '') {
        const newLink = 'https://' + link;
        validation.push(newLink);
      }
    });

    fsUpdateDoc(`surveyFeedback/${feedbackItem.id}`, {
      status: selectedStatus,
      jiraLinks: validation,
      priority: selectedPriority,
      bug: selectedBug,
      hdr: selectedHdr,
    });

    isResolved
      ? fsUpdateDoc(`surveyFeedback/${feedbackItem.id}`, {
          history: [
            ...(feedbackItem?.history ?? []),
            {
              time: new Date().getTime(),
              type: 'Re-opened ticket',
              user: {
                displayName: authCtx.user.displayName,
                email: authCtx.user.email,
              },
            },
          ],
        })
      : fsUpdateDoc(`surveyFeedback/${feedbackItem.id}`, {
          history: [
            ...(feedbackItem?.history ?? []),
            {
              time: new Date().getTime(),
              type: 'Updated feedback attributes',
              user: {
                displayName: authCtx.user.displayName,
                email: authCtx.user.email,
              },
            },
          ],
        });

    closeEditFeedback();
  };

  const jiraSection = (
    <div>
      <div className={c.modalAttribute}>
        <h3 className={c.attributeName}>Jira ticket:</h3>
        {jiraLinks.length > 1
          ? jiraLinks.map((link, index) => {
              const linkId = `jiralink-${index}`;
              return (
                <div className={c.rightChunk} key={linkId}>
                  <div className={c.multiJiraLink} key={linkId}>
                    <TextField
                      value={link}
                      onChange={(e) => handleChange(e, index)}
                    />
                    <IconButton
                      size={24}
                      icon={Icon.Trash}
                      onClick={() => {
                        removeLink(index);
                      }}
                      mode="canvas"
                      aria-expanded={editFeedbackShown}
                      aria-label="Delete Jira link"
                      aria-haspopup="dialog"
                    />
                  </div>
                </div>
              );
            })
          : jiraLinks.map((link, index) => {
              const linkId = `jiralink-${index}`;
              return (
                <div className={c.rightChunk} key={linkId}>
                  <TextField
                    value={link}
                    onChange={(e) => handleChange(e, index)}
                  />
                </div>
              );
            })}
        <div className={c.rightChunk}>
          <Button
            className={c.addLink}
            size="medium"
            variant="text"
            icon={<Icon.Plus />}
            onClick={() => addLink()}
          >
            Add another ticket
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <div>
      <ConfirmationModal
        open={confirmDelete}
        onCancel={closeConfirmDelete}
        onConfirm={closeEditFeedback}
        title="Are you sure you want to discard your changes?"
        message="Your changes will be lost."
      />
      <Modal
        open={editFeedbackShown}
        onClose={closeEditFeedback}
        dismissOnHistoryNav={true}
        ariaLabel="Feedback Status"
        ariaDescribedby={null}
      >
        <ModalSection
          title="Feedback Status"
          titleTabIndex={null}
          ariaLabelledby="editFeedbackModalTitle"
          ariaDescribedby="editFeedbackModalContent"
        >
          <div className={c.modalAttribute}>
            <h3 className={c.attributeName}>Status:</h3>
            <Dropdown
              placeholder="Select"
              data={STATUS_FILTERS.filter(
                (status) => status.value !== 'resolved'
              )}
              onChange={(e) => setSelectedStatus(e)}
              value={selectedStatus}
            />
          </div>

          <div className={c.modalAttribute}>
            <h3 className={c.attributeName}>Priority:</h3>
            <Dropdown
              placeholder="Select"
              data={priorityOptions}
              onChange={(e) => setSelectedPriority(e)}
              value={selectedPriority}
            />
          </div>

          {feedbackItem?.status === UPDATE_FEEDBACK_STATUS.IN_JIRA ||
          selectedStatus === UPDATE_FEEDBACK_STATUS.IN_JIRA ? (
            <div>{jiraSection}</div>
          ) : null}

          <div className={c.modalAttribute}>
            <h3 className={c.attributeName}>Bug:</h3>
            <Dropdown
              placeholder="Select"
              data={BUG_OPTIONS}
              onChange={(e) => setSelectedBug(e)}
              value={selectedBug}
            />
          </div>

          <div className={c.modalAttribute}>
            <h3 className={c.attributeName}>HDR:</h3>
            <Dropdown
              placeholder="Select"
              data={HDR_OPTIONS}
              onChange={(e) => setSelectedHdr(e)}
              value={selectedHdr}
            />
          </div>

          <div className={c.modalButtons}>
            <Button
              variant="text"
              size="medium"
              onClick={() => {
                openConfirmDelete();
              }}
            >
              Discard
            </Button>
            <Button
              size="medium"
              disabled={isResolved && !selectedStatus}
              onClick={() => {
                submitChanges();
              }}
            >
              Save
            </Button>
          </div>
        </ModalSection>
      </Modal>
    </div>
  );
};

export default FeedbackModal;
